/* global google */
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import eventBus from '@refapp/eventbus-js';
import mapStyleOptions from './map-style-options.json';
import PromotionBannerEvent from '../promotion/promotion-banner-event';
import Event from './google-api-event';
import { tryParseJson } from './utils';
import debounce from 'lodash.debounce';

const MIN_ZOOM = 8;

export default {
  methods: {
    /**
     * @function focusOnMarker
     * @desc Centers map on marker and opens info window for specific location
     * @param {Object} location - Location object with store data
     */
    focusOnMarker(location) {
      if (!location || !location.Latitude || !location.Longitude) return;

      this.infoWindowLocationId = location.Id;
      this.location = location;
      this.renderPopup = true;

      this.$nextTick(() => {
        const content = this.getPopupContent();

        if (this.infoWindow) {
          this.infoWindow.close();
        }

        const position = new google.maps.LatLng(location.Latitude, location.Longitude);

        this.infoWindow = new google.maps.InfoWindow({ position: position, content });

        this.infoWindow.addListener('visible', () => {
          this.infoWindow.focus();
        });

        this.infoWindow.open(this.map);

        google.maps.event.addListener(this.infoWindow, 'closeclick', this.resetPopupContent);
      });
    },
    handleSearchInAreaClick() {
      this.showSearchButton = false;
      this.searchInVisibleArea();

      const handleStoresFound = () => {
        this.sendTaggingData();
        eventBus.off(Event.StoresFoundInCurrentArea, handleStoresFound);
      };

      eventBus.on(Event.StoresFoundInCurrentArea, handleStoresFound);
    },
    sendTaggingData() {
      if (window.dataLayer && Object.keys(this.currentAreaSearchTaggingData).length > 0) {
        window.dataLayer.push(this.currentAreaSearchTaggingData);
      }
    },
    /**
     * @function bindGMapListeners
     * @desc bindGMapListeners for listen the google map event.
     */
    bindGMapListeners(data) {
      const map = this.map;
      let status = data;
      const self = this;
      this.infoWindow = new google.maps.InfoWindow();

      const debouncedDragEnd = debounce(() => {
        this.showSearchButton = true;
      }, 500);

      google.maps.event.addListener(map, 'dragend', debouncedDragEnd);

      google.maps.event.addListener(this.infoWindow, 'close', () => {
        // clear the location id whenever the infowindow closes for whatever reason
        this.infoWindowLocationId = null;
      });
      if (this.showToggle && data) {
        this.getRadius();
      } else {
        google.maps.event.addListener(map, 'idle', () => {
          if (status) {
            self.getRadius();
            status = false;
          }
        });
      }
    },
    /**
     * @function bindZoomChangeListener
     * @desc Watch zoom level change for a map
     */
    bindZoomChangeListener() {
      if (!this.map) return false;

      // debounce to avoid redundant responses
      const debouncedSearchHandler = debounce(
        () => this.searchInVisibleArea(this.storesPerPageWithZoom),
        500,
      );

      // zoom change listener
      this.map.addListener('zoom_changed', debouncedSearchHandler);
    },
    /**
     * @function getApproximateVisibleArea
     * @desc Calculates the visible map area
     */
    getApproximateVisibleArea() {
      if (!this.map) return 0;

      const bounds = this.map.getBounds();
      if (!bounds) return 0;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      // Degree diff
      const lngDiff = Math.abs(ne.lng() - sw.lng());

      // Transform degree into km
      const lngToKm = 111 * Math.cos((ne.lat() * Math.PI) / 180); // 1° lon ≈ 111 * cos(lat)
      const decayFactor = 0.7;
      const UNIT_CONVERSION_FACTORS = {
        miles: 0.621371,
        meters: 1000,
      };
      const unitKey = this.apiDatas.distanceTypes?.toLowerCase();
      const unitConversionFactor = UNIT_CONVERSION_FACTORS[unitKey] || 1;
      return lngDiff * lngToKm * decayFactor * unitConversionFactor;
    },
    /**
     * @function searchInVisibleArea
     * @desc Calculates the search radius based on the zoom level
     */
    searchInVisibleArea(storesPerPage = this.storesPerPage) {
      if (this.infoWindow) {
        this.infoWindow.close();
      }
      this.resetPopupContent();

      const currentLat = this.map.getCenter().lat();
      const currentLng = this.map.getCenter().lng();

      const zoom = this.map.getZoom();

      if (zoom < MIN_ZOOM) {
        this.map.setZoom(MIN_ZOOM);
      }
      const visibleRadius = this.getApproximateVisibleArea();

      this.triggerSearchApi(
        currentLat,
        currentLng,
        visibleRadius.toFixed(2) || 1,
        this.apiDatas.categorys || 'ALL',
        this.apiDatas.distanceTypes || 'mi',
        this.apiDatas.zip || '',
        this.apiDatas.upc || '',
        1,
        this.apiDatas.promotionData || '',
        this.apiDatas.productGroupId || '',
        this.filterByAttribute,
        storesPerPage,
      );
    },
    getWebsiteBaseData(urls) {
      return new URL(urls).origin;
    },
    async getRadius() {
      if (this.latupgrade !== this.lat || this.lonupgrade !== this.lon) {
        await this.initMap();
      }

      /**
       * NOTE: THE ARGUMENTS (ELEMENTS OF THIS ARRAY) ARE **POSITIONAL**!
       *
       * They must be specified in the same order they should be received, as individual parameters,
       * by the `triggerSearchApi` function.
       *
       * Removing or adding an argument form the function's signature, should be reflected in these two arrays.
       *
       */
      let args = [
        this.lat,
        this.lon,
        this.distances,
        'ALL',
        this.distanceTypes,
        this.zip,
        this.upcCode,
        1,
        '',
        this.productGroupId,
        this.filterByAttribute,
      ];

      if (this.distanceEnabled || this.categoryEnabled) {
        args = [
          this.lat,
          this.lon,
          this.distances,
          this.typemodel,
          this.distanceTypes,
          this.zip,
          this.upcCode,
          this.pageNo ?? 1,
          '',
          this.productGroupId,
          this.filterByAttribute,
        ];
      }

      await this.triggerSearchApi(...args);
    },
    getParameter(paramName) {
      var searchString = window.location.search.substring(1),
        i,
        val,
        params = searchString.split('&');

      for (i = 0; i < params.length; i++) {
        val = params[i].split('=');
        if (val[0] == paramName) {
          return val[1];
        }
      }
      return '';
    },
    async triggerSearchApi(
      lat,
      lon,
      distance,
      categorys,
      distanceTypes,
      zip,
      upc,
      pages,
      promotionData,
      productGroupId,
      filterByAttribute,
      storesPerPage = this.storesPerPage,
    ) {
      try {
        this.apiDatas = {
          lat: lat,
          lon: lon,
          distance: distance,
          categorys: categorys,
          distanceTypes: distanceTypes,
          zip: zip,
          upc: upc,
          promotionData: promotionData,
          productGroupId: productGroupId,
        };
        eventBus.emit('rfp_get_promotion_banner_info');
        if (this.iriEnabled) {
          this.getStoreList(
            lat,
            lon,
            distance,
            categorys,
            distanceTypes,
            zip,
            upc,
            pages,
            promotionData,
            productGroupId,
            storesPerPage,
            filterByAttribute,
          );
        } else {
          eventBus.emit('rfp-storelocator-enable-overlay');
          let radiusDatas = '';
          if (distance === undefined) {
            radiusDatas = 0;
          } else {
            radiusDatas = distance;
          }
          eventBus.emit('pagination_new_count');
          let attributeFilterQueryStr = this.getAttributeFilterQueryString(filterByAttribute);

          if (this.updateGoogleMapData) return;
          const currentLat = this.map.getCenter().lat();
          const currentLng = this.map.getCenter().lng();

          let params = new URLSearchParams({
            latitude: currentLat,
            longitude: currentLng,
            radius: radiusDatas,
            category: categorys,
            distanceMetric: distanceTypes,
            promotion: promotionData || this.getParameter('promotion'),
            languagecode: this.languageCode,
            perPage: storesPerPage,
          });

          if (attributeFilterQueryStr) {
            const attributeParams = new URLSearchParams(attributeFilterQueryStr);
            params = new URLSearchParams([...params, ...attributeParams]);
          }

          fetch(`/refapp/Storefinder/gettotalstorecount?${params}`)
            .then((resp) => {
              if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);
              return resp.text();
            })
            .then((resp) => {
              this.TotalStoreCount = tryParseJson(resp);
              eventBus.emit(Event.StoresFound);
              eventBus.emit(Event.StoresFoundInCurrentArea);
              this.getStoreList(
                currentLat,
                currentLng,
                distance,
                categorys,
                distanceTypes,
                zip,
                upc,
                pages,
                promotionData,
                productGroupId,
                storesPerPage,
                filterByAttribute,
              );
              eventBus.emit('pagination_record_count', {
                currentPage: 1,
                lastPage: Math.ceil(this.TotalStoreCount.TotalStoreCount / storesPerPage),
                TotalStoreCount: this.TotalStoreCount.TotalStoreCount,
              });
            })
            .catch((error) => {
              eventBus.emit('storeData', { value: [], page: 0 });
              this.resetMarkers();
              eventBus.emit(Event.DisableOverlay);
              eventBus.off(Event.StoresFound);
              eventBus.off(Event.StoresFoundInCurrentArea);
              throw new Error(error);
            });
        }
      } catch (error) {
        eventBus.emit(Event.DisableOverlay);
        throw new Error(error);
      }
    },
    getStoreList(
      lat,
      lon,
      distance,
      categorys,
      distanceTypes,
      zip,
      upc,
      pages,
      promotionData,
      productGroupId,
      storesPerPage = this.storesPerPage,
      filterByAttribute,
    ) {
      if (!this.productGroupEnabled) {
        productGroupId = '';
      }

      this.objMarker = [];
      this.gmarkers = [];
      this.distanceUpgrade = distance;
      this.latupgrade = lat;
      this.lonupgrade = lon;
      eventBus.emit(PromotionBannerEvent.QueryParams, this.getParameter('promotion'));
      let radiusDatas = '';
      if (distance === undefined) {
        radiusDatas = 0;
      } else {
        radiusDatas = distance;
      }
      let attributeFilterQueryStr = this.getAttributeFilterQueryString(filterByAttribute);

      if (this.updateGoogleMapData) return;

      eventBus.emit('rfp-storelocator-enable-overlay');
      eventBus.emit('rfp-distanceMatric', distanceTypes);
      let params = new URLSearchParams({
        latitude: lat,
        longitude: lon,
        radius: radiusDatas,
        category: categorys,
        distanceMetric: distanceTypes,
        zip: zip,
        upc: this.getParameter(upc),
        pageNo: pages,
        promotion: promotionData || this.getParameter('promotion'),
        productGroupId: productGroupId,
        languagecode: this.languageCode,
        perPage: storesPerPage,
      });

      if (attributeFilterQueryStr) {
        const attributeParams = new URLSearchParams(attributeFilterQueryStr);
        params = new URLSearchParams([...params, ...attributeParams]);
      }

      this.lastPerPageValue = storesPerPage;

      fetch(`/refapp/Storefinder/search?${params}`)
        .then((resp) => {
          if (!resp.ok) throw new Error(`HTTP error ${resp.status}`);
          return resp.text();
        })
        .then((resp) => {
          this.objMarker = tryParseJson(resp);
          let labelIndex = 1;
          this.iridatass = [];
          if (this.objMarker.length === 0) {
            eventBus.emit('storeData', { value: [], page: 0 });
            this.resetMarkers();
            eventBus.emit(Event.DisableOverlay);
            eventBus.emit('pagination_record_count', {
              currentPage: 0,
              lastPage: 0,
              TotalStoreCount: 0,
            });
            eventBus.off(Event.StoresFound);
          } else {
            eventBus.emit(Event.StoresFound);
            if (this.iriEnabled) {
              eventBus.emit('pagination_record_count', {
                currentPage: pages,
                lastPage: Math.ceil(this.objMarker[0].TotalStoreCount / storesPerPage),
                TotalStoreCount: this.objMarker[0].TotalStoreCount,
              });
              (async () => {
                await this.FindLatLong(this.objMarker, pages);
              })();
            } else {
              Object.keys(this.objMarker).map((object) => {
                this.objMarker[object].id = `${labelIndex++}`;
              });
              this.updateGoogleMapCenterPlace(
                this.objMarker[0].Latitude,
                this.objMarker[0].Longitude,
              );
              eventBus.emit('pagination_current_page', pages);
              eventBus.emit('storeData', { value: this.objMarker, page: pages });
              eventBus.emit(PromotionBannerEvent.ListCollection, this.objMarker);
              eventBus.emit(Event.DisableOverlay);
            }
          }
        })
        .catch((error) => {
          eventBus.emit('storeData', { value: [], page: 0 });
          eventBus.emit('salonListEmpty', pages);
          this.resetMarkers();
          eventBus.emit(Event.DisableOverlay);
          eventBus.emit('pagination_record_count', {
            currentPage: 0,
            lastPage: 0,
            TotalStoreCount: 0,
          });
          eventBus.off(Event.StoresFound);
          throw new Error(error);
        });
    },
    getAttributeFilterQueryString(filters) {
      let str = this.serializeAttributeFilter(filters);
      if (str.length > 0) {
        str = '&' + str;
      }

      return str;
    },
    serializeAttributeFilter(filters) {
      if (!filters) {
        return '';
      }

      var str = [];
      for (var f in filters) {
        if (Object.prototype.hasOwnProperty.call(filters, f) && filters[f].length) {
          str.push('attr=' + encodeURIComponent(filters[f]));
        }
      }

      return str.join('&');
    },
    async initMap() {
      const { Map: GoogleMap } = await google.maps.importLibrary('maps');
      const center = new google.maps.LatLng(this.lat, this.lon);
      const mapConfig = {
        ...this.mapConfig,
        center,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        navigationControlOptions: {
          style: google.maps.NavigationControlStyle.SMALL,
        },
        styles: mapStyleOptions,
        minZoom: MIN_ZOOM,
      };

      if (window.__WSF_GLOBALS__ && window.__WSF_GLOBALS__.GOOGLE_MAP_ID) {
        delete mapConfig.styles;
        mapConfig.mapId = window.__WSF_GLOBALS__.GOOGLE_MAP_ID;
      }

      try {
        this.map = new GoogleMap(document.getElementById('map'), mapConfig);
        this.markerClusterer = new MarkerClusterer({ markers: [], map: this.map });
        this.bindZoomChangeListener();
      } catch (e) {
        throw new Error('rfp-storelocator-google-API/ERROR: Unable to initialize map', e);
      }
    },
    FindLatLong(datas, pages) {
      if (!datas) {
        return;
      }
      datas.forEach((n) => {
        if (n.Name != 'Ø¹ÙÙØ©') {
          const geocoder = new google.maps.Geocoder();
          let address = '';
          if (n.Address2 != null) {
            address = `${n.Address1},${n.Address2},${n.City},${n.Country},${n.PostalCode}`;
          } else {
            address = `${n.Address1},${n.City},${n.Country},${n.PostalCode}`;
          }
          geocoder.geocode(
            {
              address: address,
            },
            (results, status) => {
              const isValidLocation =
                status === google.maps.GeocoderStatus.OK || status === 'OVER_QUERY_LIMIT';

              if (isValidLocation && results != null) {
                n['Latitude'] = results[0].geometry.location.lat();
                n['Longitude'] = results[0].geometry.location.lng();
              }
              this.iridatass.push(n);

              if (this.objMarker.length === this.iridatass.length) {
                // Sort list by number
                this.iridatass.sort((a, b) => (a.Number > b.Number ? 1 : -1));

                // Only update the map if Google finds directions
                if (isValidLocation) {
                  this.updateGoogleMapCenterPlace(
                    this.iridatass[0].Latitude,
                    this.iridatass[0].Longitude,
                  );
                }
                eventBus.emit('pagination_current_page', pages);
                eventBus.emit('storeData', { value: this.iridatass, page: pages });
                eventBus.emit(PromotionBannerEvent.ListCollection, this.iridatass);
                eventBus.emit(Event.DisableOverlay);
              }
            },
          );
        }
      });
    },
    resetMarkers() {
      this.gmarkers = [];
      if (!this.markerClusterer) {
        this.markerClusterer = new MarkerClusterer({ markers: [], map: this.map });
        return;
      }
      this.markerClusterer.clearMarkers();
      // Reset render popup flag to make sure it is in a closed state before updateing markers
      this.renderPopup = false;
    },
    updateMarker(data) {
      this.resetMarkers();
      const infoWin = new google.maps.InfoWindow();

      google.maps.event.addListener(infoWin, 'closeclick', this.resetPopupContent);

      const markers = data.map((location) => {
        const latlng = new google.maps.LatLng(location.Latitude, location.Longitude);

        const markerId = this.iriEnabled ? location.Number.toString() : location.id;
        const marker = this.updateGmapMarkers(latlng, markerId);
        google.maps.event.addListener(marker, 'click', () => {
          // prevent opening a duplicate infowindow for this marker
          if (this.infoWindowLocationId === location.Id) {
            return;
          }
          eventBus.on('mapPopupUpdated', () => {
            eventBus.off('mapPopupUpdated');
            this.infoWindowLocationId = location.Id;
            infoWin.setContent(this.getPopupContent());
            infoWin.open(this.map, marker);
          });
          this.location = location;
          this.renderPopup = true;
        });
        return marker;
      });

      this.markerClusterer.addMarkers(markers);

      setTimeout(() => {
        this.bindGMapListeners(false);
      }, 500);
    },
    updateGmapMarkers(latlng, storeId) {
      const markerConfig = {};

      // marker icon has higher priority
      if (this.markerIconUrl) {
        markerConfig.icon = {
          url: this.markerIconUrl,
          scaledSize: new google.maps.Size(this.markerSize, this.markerSize), // Icon size
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(this.markerSize / 2, this.markerSize),
        };
      }

      if (!this.disableListItemNumbering) {
        markerConfig.label = {
          text: storeId,
          color: this.markerLabelColor || null,
        };
      }

      const marks = new google.maps.Marker({
        map: this.map,
        position: latlng,
        ...markerConfig,
      });
      marks.storeId = storeId;
      this.gmarkers.push(marks);
      return marks;
    },
    updateGoogleMapCenterPlace(Latitude, Longitude) {
      const newCenter = new google.maps.LatLng(Latitude, Longitude);

      this.map.panTo(newCenter);
    },
    getPopupContent() {
      return document.getElementById('map-popup-content');
    },
    resetPopupContent() {
      this.infoWindowLocationId = null;
      this.location = {};
      this.renderPopup = false;
    },
  },
  /**
   * @function created
   * @desc on mounting , listen the other component Event
   */
  created() {
    eventBus.on('focusOnStoreMarker', (location) => {
      this.focusOnMarker(location);
    });
    eventBus.on('get-store-list-from-pagination', (value) => {
      this.getStoreList(
        this.apiDatas.lat,
        this.apiDatas.lon,
        this.apiDatas.distance,
        this.apiDatas.categorys,
        this.apiDatas.distanceTypes,
        this.apiDatas.zip,
        this.apiDatas.upc,
        value,
        this.apiDatas.promotionData,
        this.apiDatas.productGroupId,
        this.lastPerPageValue,
        this.filterByAttribute,
      );
    });
    eventBus.on(PromotionBannerEvent.BannerFilter, async (promotionData) => {
      await this.triggerSearchApi(
        this.apiDatas.lat,
        this.apiDatas.lon,
        this.apiDatas.distance,
        this.apiDatas.categorys,
        this.apiDatas.distanceTypes,
        this.apiDatas.zip,
        this.apiDatas.upc,
        1,
        promotionData,
        this.apiDatas.productGroupId,
      );
    });
    eventBus.on('updateMarker', this.updateMarker);
    eventBus.on('map-options', (value) => {
      this.map = value;
    });
    eventBus.on('refresh-google-map-options', this.initMap);
    eventBus.on('update-google-map-center-place', (values) => {
      if (
        values.lat != '' &&
        values.lat != undefined &&
        values.long != '' &&
        values.long != undefined
      ) {
        this.updateGoogleMapCenterPlace(values.lat, values.long);
      }
    });
    eventBus.on('updateGoogleMapDatas', (value) => {
      this.updateGoogleMapData = value;
    });
    eventBus.on('latlangUpdate', (value) => {
      this.lat = value.lat;
      this.lon = value.lon;
    });
    const resetDistanceUpgrade = () => (this.distanceUpgrade = 0);
    eventBus.on('defaultDistanceEmpty', resetDistanceUpgrade);
    eventBus.on('pageChangedEvent', (value) => {
      eventBus.emit('pageNavigationNextPrev', value);
    });
    eventBus.on('enableMap', () => {
      this.resetMarkers();
    });
    eventBus.on('rfp-distanceMatric', (value) => {
      this.distanceType = value;
    });
  },
  /**
   * @function data
   * @desc init the data when the component mount
   */
  data() {
    return {
      infoWindow: '',
      infoWindowLocationId: null,
      lat: '',
      lon: '',
      map: '',
      r: '',
      gmarkers: [],
      objMarker: [],
      distanceUpgrade: 0,
      latupgrade: '',
      lonupgrade: '',
      mapConfig: {
        zoom: 0,
        gestureHandling: 'cooperative',
        mapTypeControl: false,
        componentRestrictions: {
          country: 'us',
        },
      },
      updateGoogleMapData: false,
      iridatas: [],
      iridatass: [],
      apiDatas: {},
    };
  },
};
